import { Injectable } from '@angular/core';
import { AuthRepositoryModule } from './auth-repository.module';
import { AuthCredential } from './model/auth-credential.model';

/**
 * Responsible for storing minimal needed credentials for authenticated user.
 * Use with caution. Credentials are stored in the local storage.
 */
@Injectable({ providedIn: AuthRepositoryModule })
export class AuthStorage {
  private static readonly ACCESS_TOKEN: string = 'ACCESS_TOKEN';
  private static readonly REFRESH_TOKEN: string = 'REFRESH_TOKEN';
  private static readonly ID_TOKEN: string = 'ID_TOKEN';
  private static readonly CLIENT_USER_KEY: string = 'CLIENT_USER';
  private static readonly CLIENT_PASSWORD_KEY: string = 'CLIENT_PASSWORD';
  private static readonly SESSION: string = 'SESSION';
  private static readonly EMAIL: string = 'EMAIL';
  private static readonly CHALLENGENAME: string = 'CHALLENGE_NAME';
  private static readonly LOCAL_AUTH: string = 'LOCAL_AUTH';

  private credential?: AuthCredential;

  constructor() {}

  public setCredential(credential: AuthCredential) {
    this.credential = credential;
    if (credential.accessToken) {
      localStorage.setItem(AuthStorage.ACCESS_TOKEN, credential.accessToken);
    }

    if (credential.refreshToken) {
      localStorage.setItem(AuthStorage.REFRESH_TOKEN, credential.refreshToken);
    }

    if (credential.idToken) {
      localStorage.setItem(AuthStorage.ID_TOKEN, credential.idToken);
    }

    if (credential.challengeName) {
      localStorage.setItem(AuthStorage.CHALLENGENAME, credential.challengeName);
    }

    if (credential.email) {
      localStorage.setItem(AuthStorage.EMAIL, credential.email);
    }

    if (credential.session) {
      localStorage.setItem(AuthStorage.SESSION, credential.session);
    }

    if (credential.localAuth) {
      localStorage.setItem(AuthStorage.LOCAL_AUTH, 'true');
    } else {
      localStorage.removeItem(AuthStorage.LOCAL_AUTH);
    }
  }

  public getCredential(): AuthCredential | undefined {
    this.credential = this.readCredential();
    return this.credential;
  }

  public get cachedCredential(): AuthCredential | undefined {
    return this.credential;
  }

  /**
   * Remove everithyng from storage.
   */
  public clear() {
    this.clearCache();
    localStorage.removeItem(AuthStorage.ACCESS_TOKEN);
    localStorage.removeItem(AuthStorage.REFRESH_TOKEN);
    localStorage.removeItem(AuthStorage.ID_TOKEN);
    localStorage.removeItem(AuthStorage.EMAIL);
    localStorage.removeItem(AuthStorage.CHALLENGENAME);
    localStorage.removeItem(AuthStorage.SESSION);
    localStorage.removeItem(AuthStorage.LOCAL_AUTH);
  }

  /**
   * Clear cached variables. Next get will try to read from local storage.
   */
  public clearCache() {
    this.credential = undefined;
  }

  private readCredential(): AuthCredential | undefined {
    const accessToken = localStorage.getItem(
      AuthStorage.ACCESS_TOKEN,
    ) as string;
    const refreshToken = localStorage.getItem(
      AuthStorage.REFRESH_TOKEN,
    ) as string;
    const idToken = localStorage.getItem(AuthStorage.ID_TOKEN) as string;

    const session = localStorage.getItem(AuthStorage.SESSION) as string;

    const challengeName = localStorage.getItem(
      AuthStorage.CHALLENGENAME,
    ) as string;

    const email = localStorage.getItem(AuthStorage.EMAIL) as string;
    const localAuth = localStorage.getItem(AuthStorage.LOCAL_AUTH) === 'true';

    if (
      !accessToken &&
      !refreshToken &&
      !idToken &&
      !session &&
      !challengeName &&
      !email &&
      !localAuth
    ) {
      return undefined;
    }

    return new AuthCredential(
      accessToken,
      refreshToken,
      idToken,
      session,
      challengeName,
      email,
      localAuth,
    );

    // return undefined;
  }

  private readClientAPICredentials(): string {
    const user = atob(
      localStorage.getItem(AuthStorage.CLIENT_USER_KEY) as string,
    );
    const password = atob(
      localStorage.getItem(AuthStorage.CLIENT_PASSWORD_KEY) as string,
    );

    return btoa(user + ':' + password);
  }

  public getClientAPIAuthorizationHeader(): string {
    return 'Basic' + ' ' + this.readClientAPICredentials();
  }
}
